import type { BoardDefinition, GlobalTask } from '../types';
import { boardsStore, getBoardById } from '../stores/boardsStore';
import { globalTasksStore } from '../stores/globalTasksStore';
import { initBoardColumnsStore } from '../boardv3/stores/columnsStore';
import { initBoardTaskViewStore } from '../boardv3/stores/boardTaskViewStore';
import { initBoardSchedule, recalculateSchedule } from '../boardv3/stores/roadmapStore';
import { getNextTaskId } from '../stores/taskIdStore';

export const PLAYGROUND_BOARD_ID = '__playground__';

const COLUMNS = ['Foundation', 'Core Features', 'Polish', 'Launch'] as const;

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function makeTask(title: string, column: typeof COLUMNS[number], pert: { o: number; m: number; p: number }): { task: GlobalTask; column: string } {
  const id = getNextTaskId();
  return {
    task: {
      id,
      title,
      startDate: todayISO(),
      estimateMode: 'default',
      defaultEstimate: pert,
      estimates: {},
      assigneeIds: [],
      categoryIds: [],
    },
    column,
  };
}

const PLAYGROUND_TASKS: Array<{ title: string; column: typeof COLUMNS[number]; pert: { o: number; m: number; p: number } }> = [
  // Foundation
  { title: 'Architecture & Tech Stack Decision', column: 'Foundation', pert: { o: 2, m: 3, p: 5 } },
  { title: 'CI/CD Pipeline Setup', column: 'Foundation', pert: { o: 1, m: 2, p: 4 } },
  { title: 'Database Schema Design', column: 'Foundation', pert: { o: 3, m: 5, p: 8 } },
  // Core Features
  { title: 'User Authentication & Authorization', column: 'Core Features', pert: { o: 5, m: 8, p: 14 } },
  { title: 'REST API Implementation', column: 'Core Features', pert: { o: 8, m: 12, p: 20 } },
  { title: 'Dashboard UI', column: 'Core Features', pert: { o: 5, m: 10, p: 15 } },
  // Polish
  { title: 'Performance Optimization', column: 'Polish', pert: { o: 3, m: 5, p: 10 } },
  { title: 'Accessibility & i18n', column: 'Polish', pert: { o: 4, m: 7, p: 12 } },
  { title: 'E2E Test Suite', column: 'Polish', pert: { o: 3, m: 5, p: 8 } },
  // Launch
  { title: 'Security Audit', column: 'Launch', pert: { o: 2, m: 4, p: 7 } },
  { title: 'Documentation & Onboarding', column: 'Launch', pert: { o: 3, m: 5, p: 8 } },
  { title: 'Production Deployment', column: 'Launch', pert: { o: 1, m: 2, p: 5 } },
];

export function setupPlaygroundBoard(): void {
  // Idempotent: skip if already exists
  if (getBoardById(PLAYGROUND_BOARD_ID)) return;

  const now = new Date().toISOString();
  const board: BoardDefinition = {
    id: PLAYGROUND_BOARD_ID,
    name: 'Playground',
    startDate: todayISO(),
    createdAt: now,
    updatedAt: now,
    employeeIds: [],
  };

  // 1. Create the board
  boardsStore.send({ type: 'board.create', board });

  // 2. Create columns
  const colStore = initBoardColumnsStore(PLAYGROUND_BOARD_ID);
  for (const col of COLUMNS) {
    colStore.send({ type: 'column.add', column: col });
  }

  // 3. Create tasks and board views
  const viewStore = initBoardTaskViewStore(PLAYGROUND_BOARD_ID);
  for (const def of PLAYGROUND_TASKS) {
    const { task, column } = makeTask(def.title, def.column, def.pert);
    globalTasksStore.send({ type: 'task.add', task });
    viewStore.send({ type: 'board.addTask', taskId: task.id, column });
  }

  // 4. Initialize schedule
  initBoardSchedule(PLAYGROUND_BOARD_ID, board.startDate);
  recalculateSchedule(PLAYGROUND_BOARD_ID);
}
